import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { clearContentCache } from "@/lib/useContent";
import { formatApiError } from "@/context/AuthContext";
import {
    Save, RotateCcw, Plus, Trash2, Edit3, Check, X,
    FileText, Film, BookOpen, MapPin, Inbox, Image as ImageIcon, Upload, Navigation, UserCheck, Radio, CalendarClock, Ban, MessageSquareQuote, Star, Mic, Play,
} from "lucide-react";
import { Field } from "../shared.jsx";

const EMPTY_LOG = {
    title: "",
    body: "",
    published_at: "",
    day_label: "",
    image_url: "",
    location: "",
    mood: "",
};

// Convert ISO to <input type="datetime-local"> value (YYYY-MM-DDTHH:mm, local time)
const isoToLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const localInputToIso = (v) => (v ? new Date(v).toISOString() : "");

export default function PilotLogEditor() {
    const [entries, setEntries] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/admin/pilot-log").then((r) => setEntries(r.data));
    useEffect(() => { load(); }, []);

    const save = async (form) => {
        if (!form.title.trim() || !form.body.trim() || !form.published_at) {
            toast.error("Title, body and publish date are required.");
            return;
        }
        try {
            const payload = {
                title: form.title,
                body: form.body,
                published_at: form.published_at,
                day_label: form.day_label || "",
                image_url: form.image_url || "",
                location: form.location || "",
                mood: form.mood || "",
            };
            if (form.id) {
                await api.put(`/admin/pilot-log/${form.id}`, payload);
                toast.success("Entry updated.");
            } else {
                await api.post("/admin/pilot-log", payload);
                toast.success("Entry saved.");
            }
            setEditing(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const sendAlert = async (id) => {
        if (!window.confirm("Send an email alert about this transmission to every launch-alerts subscriber?")) return;
        try {
            const { data } = await api.post(`/admin/pilot-log/${id}/send-alert`);
            if (data.resend_enabled) {
                toast.success(`Alert sent to ${data.sent} subscriber${data.sent === 1 ? "" : "s"}.`);
            } else {
                toast.success(`Queued ${data.sent} alert${data.sent === 1 ? "" : "s"} (Resend API key not set — logged only).`);
            }
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const sendPreview = async (id) => {
        try {
            const { data } = await api.post(`/admin/pilot-log/${id}/preview-alert`);
            if (data.resend_enabled && data.delivered) {
                toast.success(`Preview sent to ${data.sent_to}. Check your inbox.`);
            } else {
                toast.success(`Preview queued for ${data.sent_to} (Resend API key not set — logged only).`);
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this log entry?")) return;
        try {
            await api.delete(`/admin/pilot-log/${id}`);
            toast.success("Deleted.");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    if (editing !== null) {
        return (
            <div className="card p-8 max-w-3xl" data-testid="pilotlog-form">
                <h2 className="font-serif text-2xl mb-6">{editing.id ? "Edit transmission" : "New transmission"}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label="Title" full>
                        <input className="input-line" value={editing.title} onChange={(e) => setEditing({...editing, title: e.target.value})} data-testid="pilotlog-form-title" placeholder="Eve of takeoff" />
                    </Field>
                    <Field label="Day label (e.g., T-Minus 7 Days, Day 3 of Flight)">
                        <input className="input-line" value={editing.day_label} onChange={(e) => setEditing({...editing, day_label: e.target.value})} placeholder="T-Minus 7 Days" />
                    </Field>
                    <Field label="Publishes at (unlocks publicly)">
                        <input
                            type="datetime-local"
                            className="input-line"
                            value={isoToLocalInput(editing.published_at)}
                            onChange={(e) => setEditing({...editing, published_at: localInputToIso(e.target.value)})}
                            data-testid="pilotlog-form-published"
                        />
                    </Field>
                    <Field label="Location (optional)">
                        <input className="input-line" value={editing.location} onChange={(e) => setEditing({...editing, location: e.target.value})} placeholder="Ninewells, Dundee" />
                    </Field>
                    <Field label="Conditions / mood (optional)">
                        <input className="input-line" value={editing.mood} onChange={(e) => setEditing({...editing, mood: e.target.value})} placeholder="clear, gusting 14 knots" />
                    </Field>
                    <Field label="Image URL (optional)" full>
                        <input className="input-line" value={editing.image_url} onChange={(e) => setEditing({...editing, image_url: e.target.value})} placeholder="https://..." />
                    </Field>
                    <Field label="Body (blank lines separate paragraphs)" full>
                        <textarea className="input-line" rows={10} value={editing.body} onChange={(e) => setEditing({...editing, body: e.target.value})} data-testid="pilotlog-form-body" />
                    </Field>
                </div>
                <div className="flex justify-between items-center gap-2 mt-8 flex-wrap">
                    <div>
                        {editing.id && (
                            <button
                                type="button"
                                onClick={() => sendPreview(editing.id)}
                                className="btn-outline text-sm"
                                style={{padding: "0.55rem 1rem"}}
                                title="Send this email only to your admin address as a preview — no subscribers receive it"
                                data-testid="pilotlog-form-preview"
                            >
                                Preview email to me
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => setEditing(null)} className="btn-outline text-sm" style={{padding: "0.55rem 1rem"}}><X size={14} /> Cancel</button>
                        <button onClick={() => save(editing)} className="btn-primary text-sm" style={{padding: "0.55rem 1rem"}} data-testid="pilotlog-form-save"><Check size={14} /> Save entry</button>
                    </div>
                </div>
            </div>
        );
    }

    const now = new Date().toISOString();
    const published = entries.filter((e) => e.published_at <= now);
    const scheduled = entries.filter((e) => e.published_at > now);

    return (
        <div data-testid="pilotlog-editor">
            <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
                <div>
                    <div className="overline mb-2">Progressive unlock</div>
                    <p className="text-sm text-[var(--sb-text-dim)] max-w-xl">
                        Entries with a future <em>Publishes at</em> stay sealed on the public page until their time arrives. Pre-schedule a countdown of transmissions.
                    </p>
                </div>
                <button onClick={() => setEditing({ ...EMPTY_LOG })} className="btn-primary" data-testid="admin-add-pilotlog">
                    <Plus size={16} /> New transmission
                </button>
            </div>

            {scheduled.length > 0 && (
                <div className="mb-8">
                    <div className="overline mb-3 text-[var(--sb-brand)]">Sealed · {scheduled.length}</div>
                    <div className="space-y-3">
                        {scheduled.map((e) => (
                            <div key={e.id} className="card p-5 flex justify-between gap-4 border-dashed" data-testid={`admin-pilotlog-${e.id}`}>
                                <div className="min-w-0">
                                    <div className="overline">{e.day_label || "Scheduled"} · unlocks {new Date(e.published_at).toLocaleString("en-GB")}</div>
                                    <div className="font-serif text-xl truncate">{e.title}</div>
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    <button onClick={() => setEditing(e)} className="text-[var(--sb-text-dim)] hover:text-[var(--sb-brand)] p-2"><Edit3 size={16} /></button>
                                    <button onClick={() => remove(e.id)} className="text-[var(--sb-text-dim)] hover:text-red-400 p-2"><Trash2 size={16} /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div>
                <div className="overline mb-3">Published · {published.length}</div>
                {published.length === 0 && scheduled.length === 0 && (
                    <div className="card p-10 text-center text-[var(--sb-text-dim)]">
                        No transmissions yet. Click <span className="text-[var(--sb-brand)]">New transmission</span> to write the first one.
                    </div>
                )}
                <div className="space-y-3">
                    {published.map((e) => (
                        <div key={e.id} className="card p-5 flex justify-between gap-4" data-testid={`admin-pilotlog-${e.id}`}>
                            <div className="min-w-0">
                                <div className="overline">{e.day_label || "Log"} · {new Date(e.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</div>
                                <div className="font-serif text-xl truncate">{e.title}</div>
                                <p className="text-sm text-[var(--sb-text-dim)] mt-1 line-clamp-2">{e.body}</p>
                                {e.alert_sent_at && (
                                    <div className="mt-2 text-[10px] tracking-[0.22em] uppercase text-[var(--sb-brand)]">
                                        Alert sent · {new Date(e.alert_sent_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · {e.alert_sent_count || 0} subscribers
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-2 shrink-0 items-start">
                                <button
                                    onClick={() => sendAlert(e.id)}
                                    title="Send email alert to all launch-alert subscribers"
                                    className="text-xs px-3 py-2 border border-[var(--sb-brand)]/40 text-[var(--sb-brand)] hover:bg-[var(--sb-brand)]/10 whitespace-nowrap"
                                    data-testid={`pilotlog-send-alert-${e.id}`}
                                >
                                    Send alert
                                </button>
                                <button onClick={() => setEditing(e)} className="text-[var(--sb-text-dim)] hover:text-[var(--sb-brand)] p-2"><Edit3 size={16} /></button>
                                <button onClick={() => remove(e.id)} className="text-[var(--sb-text-dim)] hover:text-red-400 p-2"><Trash2 size={16} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/* Bookings manager — client shoot requests + blackout dates                  */
